// MUI theme for the admin panel, built from the same brand tokens used
// to redesign the storefront (see ./designTokens.js). Scoped to only the
// admin route (see App.js) so the storefront — which styles itself via
// sx + designTokens.js directly, not theme context — is unaffected.
import { createTheme } from "@mui/material/styles";
import { colors, radii, shadows, fonts } from "./designTokens";

// A function, not a module-level constant: it must be called fresh
// (see App.js) after applyThemeColors() has mutated `colors`, so the
// theme's palette picks up admin-configured Site Design colors.
export default function buildAdminTheme() {
  return createTheme({
  palette: {
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
      light: colors.primaryLight,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.accent,
      dark: colors.accentDark,
      light: colors.accentLight,
      contrastText: colors.white,
    },
    background: {
      default: colors.paper,
      paper: colors.white,
    },
    text: {
      primary: colors.ink,
      secondary: colors.ink2,
    },
    divider: colors.line,
    // error/warning/success/info intentionally left on MUI's own defaults —
    // remapping these to brand colors would blur meaningful status signals
    // (delete/error, success confirmations) across every admin screen.
  },
  shape: {
    borderRadius: parseInt(radii.md, 10),
  },
  typography: {
    fontFamily: fonts.body,
    h1: { fontFamily: fonts.display },
    h2: { fontFamily: fonts.display },
    h3: { fontFamily: fonts.display },
    h4: { fontFamily: fonts.display },
    h5: { fontFamily: fonts.display },
    h6: { fontFamily: fonts.display },
    button: { fontFamily: fonts.body, textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          boxShadow: "none",
        },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radii.md },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: radii.lg },
        elevation1: { boxShadow: shadows.card },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          boxShadow: shadows.card,
          border: `1px solid ${colors.line}`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radii.full },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { backgroundColor: colors.primaryLight, color: colors.primary },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          color: colors.ink,
          boxShadow: shadows.card,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radii.lg },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: radii.lg,
          boxShadow: shadows.cardHover,
          border: `1px solid ${colors.line}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radii.lg,
          boxShadow: shadows.cardHover,
          border: `1px solid ${colors.line}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          margin: "2px 6px",
          width: "auto",
          "&.Mui-selected": {
            backgroundColor: colors.primaryLight,
            "&:hover": { backgroundColor: colors.primaryLight },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: radii.lg,
          boxShadow: shadows.cardHover,
          border: `1px solid ${colors.line}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.ink,
          borderRadius: radii.md,
          fontSize: 12,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          border: `1px solid ${colors.line}`,
          "&:before": { display: "none" },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radii.md },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {},
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          "&.Mui-selected": {
            backgroundColor: colors.primary,
            color: colors.white,
            "&:hover": { backgroundColor: colors.primaryDark },
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700 },
      },
    },
  },
  });
}
